import {useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Link, useHistory, useLocation} from 'react-router-dom'
import {BasicLayout} from '../../../modules/Layouts/basicLayout'
import SearchSelect from '../../../components/Inputs/SearchSelect'
import {State} from '../../../reducers'
import {ENDPOINTS} from '../../../api/config'
import * as http from '../../../api/http'
import {KEYS} from '../../../constants/keysStorage'
import {formatDateToDDMMYYYY} from '../../../utils/strings.utils'
import {renderEstadoDoc} from '../../../utils/estados'
import {obtenerTotalDocumento} from '../../../utils/total-documento.utils'
import {setActiveHistorialFactura, setAnuladoFactura} from '../../../actions/historial/historial.actions'
import {docsDatosReceptorHistorialHandleChange} from '../../../actions/documentos/documentos.actions'
import {generalOpenModalError, generalOpenModalInfo} from '../../../actions/general.actions'
import ModalConfirm from '../../../components/Modals/Generales/ModalConfirm'
import Folder from '../../../media/felsv/FolderWhite.svg'
import {Tooltip} from '../../../components/Tooltip/Tooltip'
import {PaginationStatic} from '../../../components/PaginationStatic'
import './DocumentosCliente.css'

const SESSION_KEY = 'docs_cliente_estado'

const getFechaLocal = (): string => {
  const hoy = new Date()
  const yyyy = hoy.getFullYear()
  const mm = String(hoy.getMonth() + 1).padStart(2, '0')
  const dd = String(hoy.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const DocumentosCliente = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation<{cliente?: any} | undefined>()

  const {tipoDocumentosEmision} = useSelector((state: State) => state.catalogos)
  const fechaHoy = getFechaLocal()


  const refResultados = useRef<HTMLDivElement>(null)

  const [cliente, setCliente] = useState<any>(null)
  const [fechaInicial, setFechaInicial] = useState(fechaHoy)
  const [fechaFinal, setFechaFinal] = useState(fechaHoy)
  const [tipoDTE, setTipoDTE] = useState('-1')
  const [resultados, setResultados] = useState<any[]>([])
  const [numPagina, setNumPagina] = useState(0)
  const [totalPaginas, setTotalPaginas] = useState(0)
  const [filtroGuardado, setFiltroGuardado] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [loadingExcel, setLoadingExcel] = useState(false)
  const [openModalExcel, setOpenModalExcel] = useState(false)
  const [buscado, setBuscado] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    window.scroll(0, 0)
    const clienteDesdeNavegacion = location.state?.cliente

    if (clienteDesdeNavegacion) {
      setCliente(clienteDesdeNavegacion)
      sessionStorage.removeItem(SESSION_KEY)
    } else {
      const cached = sessionStorage.getItem(SESSION_KEY)
      if (cached) {
        try {
          const estado = JSON.parse(cached)
          setCliente(estado.cliente)
          setFechaInicial(estado.fechaInicial)
          setFechaFinal(estado.fechaFinal)
          setTipoDTE(estado.tipoDTE)
          setResultados(estado.resultados)
          setNumPagina(estado.numPagina ?? 0)
          setTotalPaginas(estado.totalPaginas ?? 0)
          setFiltroGuardado(estado.filtroGuardado ?? null)
          setBuscado(true)
        } catch {
          sessionStorage.removeItem(SESSION_KEY)
        }
      }
    }
  }, [])

  const fetchDocs = async (filtro: any, page: number, docsActuales: any[]) => {
    const res: any = await http.post(
      ENDPOINTS.GET_HISTORIAL_TRANSACCIONES,
      {
        fechaInicio: filtro.fechaInicial || '-1',
        fechaFin: filtro.fechaFinal || '-1',
        tipoDoc: filtro.tipoDocumento || '-1',
        nit_emisor: '-1',
        nrc_receptor: '-1',
        nrc_emisor: '-1',
        doc_receptor: filtro.numId?.replace(/-/g, '') || '-1',
        tipodte: filtro.tipoDTE || '-1',
        NombreCompleto: '-1',
        offset: page,
        tokenGateway: localStorage.getItem(KEYS.TOKEN),
      },
      {},
      KEYS.TOKEN,
    )
    if (Array.isArray(res?.body)) {
      const taggeados = res.body
        .map((el: any) => {
          const fechaCompleta = new Date(
            `${el.DetallesDTE?.identificacion?.fecEmi}T${el.DetallesDTE?.identificacion?.horEmi}`,
          )
          return {...el, fechaCompleta, pagina: page}
        })
        .sort((a: any, b: any) => b.fechaCompleta - a.fechaCompleta)

      const nuevosTotales = page === 0 ? taggeados[0]?.Paginas ?? 0 : totalPaginas
      const nuevosResultados = page === 0 ? taggeados : [...docsActuales, ...taggeados]
      return {docs: nuevosResultados, paginas: nuevosTotales}
    }
    return null
  }

  const handleBuscar = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!cliente) return
    setLoading(true)
    setErrorMsg('')
    const filtro = {
      fechaInicial,
      fechaFinal,
      tipoDTE,
      tipoDocumento: cliente.tipoDocumento,
      numId: cliente.numId,
    }
    try {
      const resultado = await fetchDocs(filtro, 0, [])
      if (resultado) {
        setResultados(resultado.docs)
        setTotalPaginas(resultado.paginas)
        setNumPagina(0)
        setFiltroGuardado(filtro)
        guardarEnCache(resultado.docs, 0, resultado.paginas, filtro)
      } else {
        setResultados([])
        setTotalPaginas(0)
        setFiltroGuardado(filtro)
        guardarEnCache([], 0, 0, filtro)
        setErrorMsg('No se encontraron documentos para los filtros seleccionados.')
      }
    } catch (err: any) {
      setResultados([])
      setTotalPaginas(0)
      setErrorMsg(err?.response?.data?.message || 'Error al buscar documentos.')
    } finally {
      setLoading(false)
      setBuscado(true)
    }
  }

  const handlePaginacion = async (page: number) => {
    const pageOffset = page - 1
    const yaExiste = resultados.some((r) => r.pagina === pageOffset)
    let docsFinales = resultados
    if (!yaExiste && filtroGuardado) {
      setLoading(true)
      try {
        const resultado = await fetchDocs(filtroGuardado, pageOffset, resultados)
        if (resultado) {
          docsFinales = resultado.docs
          setResultados(docsFinales)
        }
      } finally {
        setLoading(false)
      }
    }
    setNumPagina(pageOffset)
    guardarEnCache(docsFinales, pageOffset, totalPaginas, filtroGuardado)
    refResultados.current?.scrollIntoView({behavior: 'smooth'})
  }

  const guardarEnCache = (docs: any[], pagina: number, paginas: number, filtro: any) => {
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({cliente, fechaInicial, fechaFinal, tipoDTE, resultados: docs, numPagina: pagina, totalPaginas: paginas, filtroGuardado: filtro}),
    )
  }

  const handleDescargarExcel = async () => {
    if (!filtroGuardado || loadingExcel) return
    setLoadingExcel(true)
    try {
      const res: any = await http.post(
        ENDPOINTS.GET_HISTORIAL_TRANSACCIONES,
        {
          fechaInicio: filtroGuardado.fechaInicial || '-1',
          fechaFin: filtroGuardado.fechaFinal || '-1',
          tipoDoc: filtroGuardado.tipoDocumento || '-1',
          nit_emisor: '-1',
          nrc_receptor: '-1',
          nrc_emisor: '-1',
          doc_receptor: filtroGuardado.numId?.replace(/-/g, '') || '-1',
          tipodte: filtroGuardado.tipoDTE || '-1',
          NombreCompleto: '-1',
          tokenGateway: localStorage.getItem(KEYS.TOKEN),
          Excel: true,
        },
        {},
        KEYS.TOKEN,
      )
      if (Array.isArray(res?.body)) {
        dispatch(generalOpenModalInfo('Proceso iniciado correctamente.'))
      } else {
        dispatch(generalOpenModalInfo('No hay resultados encontrados'))
      }
    } catch (err: any) {
      if (err?.response?.data?.errorCode === 1) {
        dispatch(generalOpenModalInfo(err?.response?.data?.message ?? 'No hay resultados encontrados'))
      } else {
        dispatch(generalOpenModalError(err?.response?.data?.message ?? 'Error al descargar Excel, intente nuevamente', err?.response?.data?.status))
      }
    } finally {
      setLoadingExcel(false)
    }
  }

  const handleVerDetalle = (transaccion: any) => {
    dispatch(setActiveHistorialFactura({...transaccion}))
    dispatch(docsDatosReceptorHistorialHandleChange({nombreTipoDocumento: '', nombre: '', numId: ''}))
    if (transaccion?.DetallesDTE?.receptorManual?.esReceptorManual === true) {
      const datos = transaccion.DetallesDTE.receptorManual
      dispatch(
        docsDatosReceptorHistorialHandleChange({
          nombreTipoDocumento: datos?.tipoDocManual ?? '',
          nombre: datos?.receptorManual,
          numId: datos?.docManual,
        }),
      )
    }
    dispatch(setAnuladoFactura(transaccion?.estadoanulacion === 'true'))
    sessionStorage.setItem('detailReturnPath', '/gestion-clientes/documentos-cliente')
    history.push('/history-invoice/detail')
  }

  const iniciales = cliente?.nombre
    ? cliente.nombre.trim().split(' ').slice(0, 2).map((w: string) => w[0]).join('').toUpperCase()
    : '?'

  return (
    <BasicLayout>
      <div className='header-link'>
        <Link to='/gestion-clientes/listar-clientes' className='back-button w-inline-block' />
        <h3 className='h3-titles xl'>Documentos del cliente</h3>
      </div>
      <div className='separador' />

      {/* ── Tarjeta del cliente ── */}
      {cliente && (
        <div className='container-white w-100 dc-cliente-card'>
          <div className='dc-cliente-avatar'>{iniciales}</div>
          <div className='dc-cliente-info'>
            <span className='dc-cliente-nombre'>{cliente.nombre}</span>
            <span className='dc-cliente-doc'>
              {cliente.nombreTipoDocumento && `${cliente.nombreTipoDocumento}: `}
              <strong>{cliente.numId ?? '—'}</strong>
            </span>
            {cliente.email && <span className='dc-cliente-extra'>{cliente.email}</span>}
          </div>
        </div>
      )}

      {/* ── Filtros de búsqueda ── */}
      <div className='container-white w-100 dc-filtros-card'>
        <h4 className='h4-titles' style={{marginBottom: '16px'}}>Filtros de búsqueda</h4>
        <form onSubmit={handleBuscar} className='dc-form'>
          <div className='dc-form-field'>
            <label className='field-label m'>Fecha inicial</label>
            <input
              type='date'
              className='m text-field normal w-input'
              value={fechaInicial}
              max={fechaHoy}
              onChange={(e) => setFechaInicial(e.target.value)}
            />
          </div>
          <div className='dc-form-field'>
            <label className='field-label m'>Fecha final</label>
            <input
              type='date'
              className='m text-field normal w-input'
              value={fechaFinal}
              max={fechaHoy}
              onChange={(e) => setFechaFinal(e.target.value)}
            />
          </div>
          <div className='dc-form-field dc-form-field--select'>
            <label className='field-label m'>Tipo de DTE</label>
            <SearchSelect
              defaultValue={null}
              placeholder='Tipo de documento'
              data={[{nombre: 'Mostrar todos', codigodocumento: '-1'}, ...tipoDocumentosEmision]}
              value={tipoDTE}
              name='tipoDTE'
              keyNameText='nombre'
              keyNameValue='codigodocumento'
              disabled={false}
              onChange={(value: any) => setTipoDTE(value?.value ?? '-1')}
            />
          </div>
          <div className='dc-form-field dc-form-field--btn'>
            <button type='submit' className='m button w-inline-block' disabled={loading || !cliente}>
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </div>
        </form>
        {errorMsg && <p className='alert s' style={{marginTop: '8px', marginBottom: 0}}>{errorMsg}</p>}
      </div>

      {/* ── Resultados ── */}
      {buscado && !loading && (
        <div className='container-white w-100' ref={refResultados}>
          {openModalExcel && (
            <ModalConfirm
              title='Generar documentos del cliente'
              detail={(<>¿Deseas generar el historial de documentos en formato .xlsx? <br/><br/> <b>Nota</b>: Al finalizar el proceso, se enviará un correo electrónico con el enlace de descarga. El tiempo de entrega del correo dependerá de la cantidad de documentos a procesar.</>)}
              messageAction='Descargar'
              actionConfirm={() => { handleDescargarExcel(); setOpenModalExcel(false) }}
              messageClose='Cancelar'
              closeModalConfirm={() => setOpenModalExcel(false)}
              Styles={{minWidth: '25%', maxWidth: '550px'}}
            />
          )}
          {resultados.length > 0 && (
            <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'end'}}>
              <Tooltip content='Descargar Historial'>
                <button
                  className='m button botton-footer'
                  style={{width: '50px', minWidth: '50px'}}
                  onClick={() => setOpenModalExcel(true)}
                  disabled={loadingExcel}
                >
                  <img src={Folder} alt='Download folder' />
                </button>
              </Tooltip>
            </div>
          )}
          <h4 className='h4-titles'>
            {resultados.length === 0 ? 'Sin documentos encontrados' : 'Resultados obtenidos'}
          </h4>
          {resultados.length > 0 && (
            <div className='tab-plane' style={{overflowX: 'auto'}}>
              <table className='tlu-table-main-responsive tlu-responsive-table-main td-left table-pointer'>
                <thead>
                  <tr>
                    <td>Correlativo</td>
                    <td>Tipo DTE</td>
                    <td>Estado</td>
                    <td>Fecha</td>
                    <td>Total</td>
                  </tr>
                </thead>
                <tbody>
                  {resultados.filter((r) => r.pagina === numPagina).map((t, i) => (
                    <tr key={i} onClick={() => handleVerDetalle(t)}>
                      <td data-label='Correlativo'>{t?.DetallesDTE?.identificacion?.numeroControl}</td>
                      <td data-label='Tipo DTE'>
                        {tipoDocumentosEmision.find(
                          (dte) => dte.codigodocumento === t?.DetallesDTE?.identificacion?.tipoDte,
                        )?.nombre || 'Indefinido'}
                      </td>
                      <td data-label='Estado'>{renderEstadoDoc(t, {esTransaccion: true})}</td>
                      <td data-label='Fecha'>
                        {formatDateToDDMMYYYY(t?.DetallesDTE?.identificacion?.fecEmi || '')}
                      </td>
                      <td data-label='Total'>
                        {obtenerTotalDocumento(t?.DetallesDTE?.identificacion?.tipoDte, t)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PaginationStatic pagesDefault={totalPaginas} onChangeCurrentPage={handlePaginacion} items={20} />
            </div>
          )}
        </div>
      )}
    </BasicLayout>
  )
}

export default DocumentosCliente
